import { isSessionPatientLinkingReadOnly } from '@/hooks/integrations/isSessionPatientLinkingReadOnly';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { useQuerySessionById } from '@heidi/session/data';

/**
 * True when the given session is EHR-integration read-only, i.e. the consult
 * was pulled from the EHR schedule (`ehr_appt_id`) or launched inside the EHR
 * iframe (`isFhirAuth`). This is the session-scoped signal that hides patient
 * mutations (unlink / delete) to match the legacy edit modal.
 *
 * - Returns `false` when there is no session in context (e.g. the profile
 *   opened from `/patients`), so those surfaces keep their actions.
 * - Returns `true` while the session query is still loading (fail-closed) so a
 *   read-only action never flashes visible before the fetch settles. Safe
 *   because `useQuerySessionById` is disabled (never loading) without a
 *   session id.
 */
export const useIsSessionEhrReadOnly = (sessionId: string | null): boolean => {
  const isFhirAuth = useIsFhirAuth();
  const { data: session, isLoading } = useQuerySessionById({
    sessionId: sessionId ?? '',
    select: (data) => (data ? { ehr_appt_id: data.ehr_appt_id } : undefined),
  });

  if (!sessionId) return false;
  if (isLoading) return true;
  return isSessionPatientLinkingReadOnly({
    ehr_appt_id: session?.ehr_appt_id,
    isFhirAuth,
  });
};
